
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { questions } from "./quizQuestions";
import QuizResults from "./QuizResults";

export const CookQuiz = ({ onComplete }: { onComplete: () => void }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<(number | number[])[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<number | number[] | null>(null);
  const [showResults, setShowResults] = useState(false);
  const { toast } = useToast();

  const calculateScore = (userAnswers: (number | number[])[]) => {
    let totalScore = 0;
    userAnswers.forEach((answer, index) => {
      const correctAnswer = questions[index].correctAnswer;
      if (Array.isArray(correctAnswer) && Array.isArray(answer)) {
        if (correctAnswer.length === answer.length && 
            correctAnswer.every(val => answer.includes(val))) {
          totalScore += 1;
        }
      } else if (!Array.isArray(correctAnswer) && !Array.isArray(answer)) {
        if (answer === correctAnswer) {
          totalScore += 1;
        }
      }
    });
    return totalScore;
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
      setSelectedAnswer(answers[currentQuestion - 1] || null);
    }
  };

  const handleAnswer = async () => {
    if (selectedAnswer === null) return;

    const newAnswers = [...answers];
    newAnswers[currentQuestion] = selectedAnswer;
    setAnswers(newAnswers);

    if (currentQuestion === questions.length - 1) {
      const score = calculateScore(newAnswers);
      const percentage = Math.round((score / questions.length) * 100);

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { error } = await supabase
          .from('cook_verifications')
          .upsert({
            cook_id: user.id,
            quiz_score: percentage,
            quiz_answers: newAnswers,
            quiz_completed_at: new Date().toISOString(),
            training_completed: percentage >= 77,
            training_completed_at: percentage >= 77 ? new Date().toISOString() : null,
            status: percentage >= 77 ? 'pending' : 'in_progress'
          }, {
            onConflict: 'cook_id'
          });

        if (error) throw error;
        setShowResults(true);

        toast({
          title: percentage >= 77 ? "Congratulations!" : "Quiz Failed",
          description: percentage >= 77 
            ? `You passed the quiz with ${percentage}%!`
            : `You got ${percentage}%. You need at least 77% to pass.`,
          variant: percentage >= 77 ? "default" : "destructive",
        });
      } catch (error: any) {
        toast({
          title: "Error",
          description: "Failed to save quiz results. Please try again.",
          variant: "destructive",
        });
      }
    } else {
      setSelectedAnswer(null);
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleResultsClose = () => {
    const score = calculateScore(answers);
    const passedQuiz = (score / questions.length) >= 0.77;
    
    if (passedQuiz) {
      onComplete();
    } else {
      setCurrentQuestion(0);
      setAnswers([]);
      setSelectedAnswer(null);
      setShowResults(false);
    }
  };

  if (showResults) {
    return <QuizResults answers={answers} onClose={handleResultsClose} />;
  }

  const question = questions[currentQuestion];
  const isMultipleChoice = Array.isArray(question.correctAnswer);

  const handleMultipleChoiceChange = (optionIndex: number) => {
    setSelectedAnswer((prev: number[] | null) => {
      const currentSelection = prev || [];
      if (currentSelection.includes(optionIndex)) {
        return currentSelection.filter(i => i !== optionIndex);
      }
      return [...currentSelection, optionIndex];
    });
  };

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Question {currentQuestion + 1} of {questions.length}</span>
            <span>Progress: {Math.round((currentQuestion / questions.length) * 100)}%</span>
          </div>

          <h3 className="text-lg font-medium text-foreground" dangerouslySetInnerHTML={{ __html: question.text }} />

          {isMultipleChoice ? (
            <div className="space-y-3">
              {question.options.map((option, index) => (
                <div key={`${currentQuestion}-${index}`} className="flex items-center space-x-2">
                  <Checkbox
                    id={`option-${currentQuestion}-${index}`}
                    checked={(selectedAnswer as number[] || []).includes(index)}
                    onCheckedChange={() => handleMultipleChoiceChange(index)}
                  />
                  <Label 
                    htmlFor={`option-${currentQuestion}-${index}`}
                    className="text-foreground"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </div>
          ) : (
            <RadioGroup
              value={selectedAnswer?.toString() || ""}
              onValueChange={(value) => setSelectedAnswer(parseInt(value))}
              className="space-y-3"
            >
              {question.options.map((option, index) => (
                <div key={`${currentQuestion}-${index}`} className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={index.toString()}
                    id={`option-${currentQuestion}-${index}`}
                  />
                  <Label 
                    htmlFor={`option-${currentQuestion}-${index}`}
                    className="text-foreground"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}

          <div className="pt-4 flex justify-between">
            <Button 
              variant="outline"
              onClick={handleBack}
              disabled={currentQuestion === 0}
            >
              Back
            </Button>
            <Button 
              onClick={handleAnswer}
              disabled={selectedAnswer === null || (Array.isArray(selectedAnswer) && selectedAnswer.length === 0)}
            >
              {currentQuestion === questions.length - 1 ? "Submit" : "Next"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CookQuiz;
